// Chain + brand icons used in the navbar + Connect-page wallet rows.
// We use `@web3icons/react`'s canonical SVGs — the upstream package
// ships the official brand marks for every supported network/token,
// MIT-licensed. Per-chain pick:
//
//   - NEAR     → NetworkNearProtocol      (the black-on-mint N tile)
//   - EVM      → NetworkEthereum          (representative for the EVM row)
//   - Solana   → NetworkSolana            (purple gradient bars)
//   - Bitcoin  → TokenBTC                 (orange ₿ disc)
//   - Starknet → NetworkStarknet          (dark hex with orange ring)
//   - Zcash    → TokenZEC                 (yellow Z disc)
//
// `variant="branded"` gives the full-color brand mark instead of a
// monochrome silhouette. The OmniBridge Ops navbar logo reuses the
// NEAR network icon — the project's identity is NEAR-anchored, so the
// brand N is the canonical mark.

import type { ReactElement } from "react";
import {
  type BaseIconProps,
  NetworkEthereum,
  NetworkNearProtocol,
  NetworkSolana,
  NetworkStarknet,
  TokenBTC,
  TokenZEC,
} from "@web3icons/react";

type IconProps = BaseIconProps;

/** Banner logo — the NEAR network mark serves as the OmniBridge Ops
 *  brand because the project's identity sits on NEAR. */
export function OmniBridgeLogo(props: IconProps) {
  return <NetworkNearProtocol variant="branded" {...props} />;
}

export function NearIcon(props: IconProps) {
  return <NetworkNearProtocol variant="branded" {...props} />;
}

/** The EVM row covers six chains; use the Ethereum mark as the canonical
 *  stand-in (matches what most multi-chain dapps do). */
export function EvmIcon(props: IconProps) {
  return <NetworkEthereum variant="branded" {...props} />;
}

export function SolanaIcon(props: IconProps) {
  return <NetworkSolana variant="branded" {...props} />;
}

export function BitcoinIcon(props: IconProps) {
  return <TokenBTC variant="branded" {...props} />;
}

export function StarknetIcon(props: IconProps) {
  return <NetworkStarknet variant="branded" {...props} />;
}

export function ZcashIcon(props: IconProps) {
  return <TokenZEC variant="branded" {...props} />;
}

/** Sun / moon glyphs for the theme toggle. Plain `currentColor` strokes so
 *  they inherit the button's text color in both themes. */
export function SunIcon(props: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

export function MoonIcon(props: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

/** Look up the icon component for a wallet/chain kind. */
export const KIND_ICON: Record<string, (props: IconProps) => ReactElement> = {
  near: NearIcon,
  evm: EvmIcon,
  sol: SolanaIcon,
  btc: BitcoinIcon,
  starknet: StarknetIcon,
  zcash: ZcashIcon,
};
